import { createFileRoute, Link, useNavigate } from "@tanstack/react-router";
import { useEffect, useState } from "react";
import { useServerFn } from "@tanstack/react-start";
import { Plus, Trash2, Lock, Globe } from "lucide-react";
import { supabase } from "@/integrations/supabase/client";
import { listMyPlaylists, createPlaylist, deletePlaylist, updatePlaylist } from "@/lib/playlists.functions";

export const Route = createFileRoute("/playlists/")({
  head: () => ({
    meta: [
      { title: "Your Playlists · KAfrequency" },
      { name: "description", content: "Personal collections of KAfrequency daily activations." },
      { name: "robots", content: "noindex" },
    ],
  }),
  component: PlaylistsIndex,
});

interface Playlist { id: string; slug: string; title: string; description: string | null; is_public: boolean; updated_at: string }

function PlaylistsIndex() {
  const nav = useNavigate();
  const [lists, setLists] = useState<Playlist[] | null>(null);
  const [title, setTitle] = useState("");
  const fetchLists = useServerFn(listMyPlaylists);
  const create = useServerFn(createPlaylist);
  const del = useServerFn(deletePlaylist);
  const upd = useServerFn(updatePlaylist);

  useEffect(() => {
    (async () => {
      const { data } = await supabase.auth.getUser();
      if (!data.user) { nav({ to: "/auth" }); return; }
      const rows = await fetchLists();
      setLists(rows as Playlist[]);
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  async function add() {
    if (!title.trim()) return;
    const pl = await create({ data: { title: title.trim() } });
    setTitle("");
    setLists((prev) => (prev ? [pl as Playlist, ...prev] : [pl as Playlist]));
  }

  async function remove(id: string) {
    await del({ data: { id } });
    setLists((prev) => prev?.filter((l) => l.id !== id) ?? []);
  }

  async function togglePublic(pl: Playlist) {
    const next = !pl.is_public;
    await upd({ data: { id: pl.id, is_public: next } });
    setLists((prev) => prev?.map((l) => l.id === pl.id ? { ...l, is_public: next } : l) ?? []);
  }

  return (
    <div className="mx-auto max-w-4xl px-4 py-16">
      <p className="text-xs uppercase tracking-[0.3em] text-gold">Sacred Collections</p>
      <h1 className="mt-3 font-serif text-4xl md:text-5xl">Your Playlists</h1>
      <p className="mt-3 max-w-xl text-muted-foreground">Curate sequences of activations for practice cycles, teachings, or offerings.</p>
      <p className="mt-3 text-xs uppercase tracking-[0.2em] text-muted-foreground">
        Looking for source material?{" "}
        <Link to="/library" className="text-gold underline decoration-gold/40 underline-offset-4 hover:text-gold-luminous hover:decoration-gold">Browse the Library →</Link>
      </p>

      <div className="mt-8 flex gap-2">
        <input value={title} onChange={(e) => setTitle(e.target.value)} placeholder="Name a new playlist"
          className="flex-1 rounded-md border hairline bg-obsidian px-3 py-2 text-sm focus:outline-none focus:ring-1 focus:ring-gold" />
        <button onClick={add} disabled={!title.trim()}
          className="inline-flex items-center gap-2 rounded-md border hairline px-4 text-xs uppercase tracking-[0.2em] hover:border-gold hover:text-gold disabled:opacity-50">
          <Plus size={14} /> Create
        </button>
      </div>

      <div className="mt-8 grid gap-3">
        {lists === null && <p className="text-sm text-muted-foreground">Loading…</p>}
        {lists?.length === 0 && <p className="text-sm text-muted-foreground">No playlists yet. Create your first collection above.</p>}
        {lists?.map((l) => (
          <div key={l.id} className="flex items-center justify-between rounded-xl border hairline bg-charcoal p-4">
            <Link to="/playlists/$id" params={{ id: l.id }} className="min-w-0">
              <p className="truncate font-serif text-lg text-ivory">{l.title}</p>
              {l.description && <p className="mt-1 line-clamp-1 text-xs text-muted-foreground">{l.description}</p>}
            </Link>
            <div className="flex items-center gap-2">
              <button onClick={() => togglePublic(l)} title={l.is_public ? "Public" : "Private"}
                className="inline-flex h-9 w-9 items-center justify-center rounded-full border hairline hover:border-gold hover:text-gold">
                {l.is_public ? <Globe size={14} /> : <Lock size={14} />}
              </button>
              <button onClick={() => remove(l.id)} className="inline-flex h-9 w-9 items-center justify-center rounded-full border hairline hover:border-red-400 hover:text-red-400">
                <Trash2 size={14} />
              </button>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}
